import { client, remote } from '../operations';
import { networkOnly, cacheAndNetworkFlat } from './generic';
import { store as storedStore } from '../../app/apollo-client-instance';
import { getClientState } from './state';
import { HOME_SIZES } from '../../constants';

export const getEstimates = store =>
  store
    .query(client.moveIqEstimates())
    .then(({ data }) => (data && data.moveIqEstimates) || []);

export const getRemoteEstimates = (
  store = storedStore(),
  { isNetworkOnly } = {}
) =>
  store.query(client.currentMove()).then(({ data: { CurrentMove: { id } } }) =>
    (isNetworkOnly !== false
      ? store
          .query(networkOnly(remote.moveIqEstimateRequest(id)))
          .then(({ data, errors }) => {
            if (
              errors &&
              errors.length &&
              errors[0].trace.code !== 'EstimateNotFound'
            ) {
              throw new Error(`MoveIqMove Error ${JSON.stringify(errors)}`);
            }
            return data.moveIqEstimateRequest;
          })
      : cacheAndNetworkFlat(
          store.query,
          remote.moveIqEstimateRequest(id),
          'moveIqEstimateRequest'
        )
    ).then(data => (data ? data.estimates : []))
  );

export const getCompanyEstimatesByStatus = (
  allCompanies,
  estimates,
  status
) => {
  const estimateLookUp = estimates.reduce(
    (obj, estimate) => ({
      ...obj,
      ...(estimate.status === status ? { [estimate.companyId]: estimate } : {}),
    }),
    {}
  );
  const companies = allCompanies.filter(({ id }) => estimateLookUp[id]);
  return companies;
};

/**
 * Given an apollo store and new estimates,
 * this helper function will retrieve any existing estimates in the cache,
 * and update/merge the new estimates with the existing
 *
 * you can also pass a "replace" option to remove all items of a given status
 *
 * @param {*} store the apollo store
 * @param {*} estimates the array of updated/new estimates
 * @param {*} options.replace replace an entire status entry (remove all items of that status)
 */
export const updateMoveIqEstimates = (store, estimates, { replace } = {}) =>
  getEstimates(store)
    .then(existingEstimates =>
      existingEstimates
        .filter(({ status }) => !replace || status !== replace)
        .reduce((updatedEstimates, { companyId, status }) => {
          const estimate = updatedEstimates.filter(
            ({ companyId: id }) => companyId === id
          )[0];
          if (!estimate) {
            updatedEstimates.push({
              companyId,
              status,
            });
          }
          return updatedEstimates;
        }, estimates)
    )
    .then(estimates => store.mutate(client.setMoveIqEstimate(estimates)));

export const getCompaniesForEstimates = (
  store = storedStore(),
  networkPolicyFunc = config => config
) =>
  getClientState(store).then(
    ({
      moveIqMove: { fromAddressDetails, toAddressDetails, moveDate },
      currentMove: { moveType },
    }) => {
      const move = {
        fromAddress: fromAddressDetails.address,
        toAddress: toAddressDetails.address,
        date: moveDate,
      };

      // NoCoverage === NoCompanies
      if (!moveType) {
        return Promise.resolve([]);
      }

      return store
        .query(networkPolicyFunc(remote.moveIqSurveyAvailability(move)))
        .then(({ data: { moveIqSurveyAvailability }, errors }) => {
          if (errors && errors.length) {
            throw new Error(`MoveIqMove Error ${JSON.stringify(errors)}`);
          }
          return moveIqSurveyAvailability || [];
        });
    }
  );

// we only need to make the priceRanges request once
// because it will supply us with all the ranges per weight
// use this variable to store the homesize map so subsequent
// function calls can use this static map instead
let cachedPriceRangesForEstimates;

// TODO: Revisit this from an architecture perspective
// A) consolidate with ReduxStore or find the right pattern
// B) do we still need the caching?
export const getPriceRangesForEstimates = (
  store = storedStore(),
  homeSize = HOME_SIZES.ONE_BEDROOM
) =>
  (cachedPriceRangesForEstimates
    ? Promise.resolve(cachedPriceRangesForEstimates)
    : getClientState(store).then(({ currentMove: { id: moveId } }) =>
        store
          .query(networkOnly(remote.searchMoveIqBallparkPrices({ moveId })))
          .then(data => {
            cachedPriceRangesForEstimates = data;
            return cachedPriceRangesForEstimates;
          })
      )
  ).then(({ data: { searchMoveIqBallparkPrices: { ballparkPrices } } }) => {
    const range = ballparkPrices.filter(item => item.homeSize === homeSize)[0];

    return range
      ? {
          minPrice: range.minPrice,
          maxPrice: range.maxPrice,
        }
      : {};
  });
